/** @deprecated 旧版节点模式组件，已被 GenerateNode + CardContentRenderer 统一架构替代。请勿在此添加新功能 */
import React, { useState, memo } from 'react';
import { AppNode, NodeStatus } from '@/types';
import { NodeHeader, InputPorts, OutputPorts, ModelSelector, PromptInput, GenerateButton, MediaPreview } from '../parts';
import { Film, ChevronDown } from 'lucide-react';

interface VideoModeProps {
  node: AppNode;
  onUpdate: (id: string, data: Partial<AppNode['data']>) => void;
  onAction: (id: string, prompt?: string) => void;
  onDelete: (id: string) => void;
  isConnecting?: boolean;
  onPortMouseDown: (e: React.MouseEvent, id: string, type: 'input' | 'output') => void;
  onPortMouseUp: (e: React.MouseEvent, id: string, type: 'input' | 'output') => void;
  inputAssets?: { id: string; type: 'image' | 'video' | 'audio'; src: string }[];
}

const VIDEO_ASPECT_RATIOS = ['1:1', '3:4', '4:3', '9:16', '16:9'];
const VIDEO_RESOLUTIONS = ['480p', '720p', '1080p'];

const VideoModeInner: React.FC<VideoModeProps> = ({
  node,
  onUpdate,
  onAction,
  onDelete,
  isConnecting,
  onPortMouseDown,
  onPortMouseUp,
  inputAssets = [],
}) => {
  const [prompt, setPrompt] = useState(node.data.prompt || '');
  const isWorking = node.status === NodeStatus.WORKING;

  const handlePromptChange = (value: string) => {
    setPrompt(value);
    onUpdate(node.id, { prompt: value });
  };

  const handleGenerate = () => {
    onAction(node.id, prompt);
  };

  const handleModelChange = (modelId: string) => {
    onUpdate(node.id, { model: modelId });
  };

  const handleAspectRatioChange = (ratio: string) => {
    onUpdate(node.id, { aspectRatio: ratio });
  };

  const handleResolutionChange = (res: string) => {
    onUpdate(node.id, { resolution: res });
  };

  return (
    <div className="relative">
      <NodeHeader
        node={node}
        onUpdate={onUpdate}
        onDelete={onDelete}
      />
      
      <InputPorts
        node={node}
        isConnecting={isConnecting}
        onPortMouseDown={onPortMouseDown}
        onPortMouseUp={onPortMouseUp}
      />
      
      <div className="p-3 flex flex-col gap-2">
        {inputAssets.length > 0 && (
          <div className="flex gap-1 mb-2">
            {inputAssets.slice(0, 4).map((asset, i) => (
              <div key={asset.id} className="w-12 h-12 rounded overflow-hidden border border-white/20">
                {asset.type === 'video' ? (
                  <video src={asset.src} className="w-full h-full object-cover" />
                ) : (
                  <img src={asset.src} className="w-full h-full object-cover" alt={`输入 ${i + 1}`} />
                )}
              </div>
            ))}
          </div>
        )}
        
        <MediaPreview
          type="video"
          status={node.status}
          src={node.data.videoUri || ''}
          error={node.data.error}
        />
        
        <PromptInput
          value={prompt}
          placeholder="描述您想生成的视频内�?.."
          onChange={handlePromptChange}
          onSubmit={handleGenerate}
          disabled={isWorking}
        />
        
        <div className="flex items-center justify-between gap-2">
          <ModelSelector
            mode="video"
            selectedModel={node.data.model || ''}
            onChange={handleModelChange}
            disabled={isWorking}
          />
          
          <div className="flex items-center gap-1">
            <div className="relative group">
              <div className="flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5 cursor-pointer text-[10px] font-bold text-slate-400">
                <Film size={12} />
                <span>{node.data.aspectRatio || '16:9'}</span>
                <ChevronDown size={10} />
              </div>
              <div className="absolute bottom-full left-0 pb-2 w-20 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200 z-[200]">
                <div className="bg-[#1c1c1e] border border-white/10 rounded-xl shadow-xl overflow-hidden">
                  {VIDEO_ASPECT_RATIOS.map(r => (
                    <div
                      key={r}
                      onClick={() => handleAspectRatioChange(r)}
                      className={`px-3 py-2 text-[10px] font-bold cursor-default hover:bg-white/10 ${
                        node.data.aspectRatio === r ? 'text-cyan-400 bg-white/5' : 'text-slate-400'
                      }`}
                    >
                      {r}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <GenerateButton
            status={node.status}
            onClick={handleGenerate}
          />
        </div>
      </div>
      
      <OutputPorts
        node={node}
        isConnecting={isConnecting}
        onPortMouseDown={onPortMouseDown}
        onPortMouseUp={onPortMouseUp}
      />
    </div>
  );
};

export const VideoMode = memo(VideoModeInner, (prevProps, nextProps) => {
  return (
    prevProps.node.id === nextProps.node.id &&
    prevProps.node.status === nextProps.node.status &&
    prevProps.node.data?.model === nextProps.node.data?.model &&
    prevProps.node.data?.videoUri === nextProps.node.data?.videoUri &&
    prevProps.node.data?.prompt === nextProps.node.data?.prompt &&
    prevProps.node.data?.error === nextProps.node.data?.error &&
    prevProps.node.data?.aspectRatio === nextProps.node.data?.aspectRatio &&
    prevProps.node.data?.resolution === nextProps.node.data?.resolution &&
    prevProps.node.data?.duration === nextProps.node.data?.duration &&
    prevProps.isConnecting === nextProps.isConnecting
  );
});